<template>
  <ResponsiveConfirmDialog
    :show="show"
    :title="action.name"
    :size="action.modalSize"
    :modal-style="action.modalStyle"
    :use-focus-trap="false"
    role="dialog"
    @close-via-escape="handlePreventModalAbandonmentOnClose"
    @close="$emit('close')"
  >
    <form
      ref="theForm"
      autocomplete="off"
      @change="onUpdateFormStatus"
      @submit.prevent.stop="$emit('confirm')"
      :data-form-unique-id="formUniqueId"
      class="panel-confirm-dialog max-md:!bg-transparent bg-white dark:bg-zinc-900 dark:max-md:!bg-transparent"
      :class="{
        'overflow-hidden rounded-3xl border border-zinc-200/70 shadow-2xl dark:border-zinc-800 max-md:rounded-none max-md:border-0 max-md:shadow-none':
          action.modalStyle === 'window',
        'flex h-full flex-col justify-between': action.modalStyle === 'fullscreen',
      }"
    >
      <div
        class="space-y-4"
        :class="{
          'overflow-hidden overflow-y-auto': action.modalStyle === 'fullscreen',
        }"
      >
        <ModalHeader class="max-md:hidden" v-text="action.name" />

        <div class="panel-confirm-dialog__body">
          <p
            v-if="action.confirmText"
            class="panel-confirm-dialog__message text-sm leading-relaxed md:px-6"
            :class="{ 'text-red-500 dark:text-red-400': action.destructive }"
          >
            {{ action.confirmText }}
          </p>

          <div v-if="action.fields.length > 0" class="max-md:px-0 px-6 pb-2">
            <div
              class="action"
              v-for="field in action.fields"
              :key="field.attribute"
            >
              <component
                :is="'form-' + field.component"
                :errors="errors"
                :resource-name="resourceName"
                :field="field"
                :show-help-text="true"
                :form-unique-id="formUniqueId"
                :mode="
                  action.modalStyle === 'fullscreen'
                    ? 'action-fullscreen'
                    : 'action-modal'
                "
                :sync-endpoint="syncEndpoint"
                @field-changed="onUpdateFieldStatus"
              />
            </div>
          </div>
        </div>
      </div>

      <ModalFooter class="max-md:hidden">
        <div class="panel-confirm-footer-actions ml-auto flex w-full items-center">
          <CancelButton
            component="button"
            type="button"
            dusk="cancel-action-button"
            class="ml-auto mr-3"
            @click="$emit('close')"
          >
            {{ action.cancelButtonText }}
          </CancelButton>

          <Button
            type="submit"
            ref="runButton"
            dusk="confirm-action-button"
            :loading="working"
            variant="solid"
            :state="action.destructive ? 'danger' : 'default'"
          >
            {{ action.confirmButtonText }}
          </Button>
        </div>
      </ModalFooter>
    </form>

    <template #footer>
      <form
        class="w-full"
        @submit.prevent.stop="$emit('confirm')"
      >
        <div class="panel-confirm-footer-actions panel-confirm-footer-actions--sheet">
          <CancelButton
            component="button"
            type="button"
            class="flex-1"
            dusk="cancel-action-button"
            @click="$emit('close')"
          >
            {{ action.cancelButtonText }}
          </CancelButton>

          <Button
            type="submit"
            class="flex-1"
            dusk="confirm-action-button"
            :loading="working"
            variant="solid"
            :state="action.destructive ? 'danger' : 'default'"
          >
            {{ action.confirmButtonText }}
          </Button>
        </div>
      </form>
    </template>
  </ResponsiveConfirmDialog>
</template>

<script>
import { PreventsModalAbandonment } from '@/mixins'
import isObject from 'lodash/isObject'
import { uid } from 'uid/single'
import { Button } from 'laravel-nova-ui'

export default {
  components: {
    Button,
  },

  emits: ['confirm', 'close'],

  mixins: [PreventsModalAbandonment],

  props: {
    action: { type: Object, required: true },
    endpoint: { type: String, required: false },
    errors: { type: Object, required: true },
    resourceName: { type: String, required: true },
    selectedResources: { type: [Array, String], required: true },
    show: { type: Boolean, default: false },
    working: Boolean,
  },

  data: () => ({
    formUniqueId: uid(),
  }),

  created() {
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {
    onUpdateFormStatus() {
      this.updateModalStatus()
    },

    onUpdateFieldStatus() {
      this.onUpdateFormStatus()
    },

    handlePreventModalAbandonmentOnClose(event) {
      this.handlePreventModalAbandonment(
        () => {
          this.$emit('close')
        },
        () => {
          event.stopPropagation()
        }
      )
    },

    handleKeydown(e) {
      if (
        !this.show ||
        e.key !== 'Enter' ||
        (!e.metaKey && !e.ctrlKey)
      ) {
        return
      }

      this.$refs.theForm?.requestSubmit?.()
    },
  },

  computed: {
    syncEndpoint() {
      let searchParams = new URLSearchParams({ action: this.action.uriKey })

      if (this.selectedResources === 'all') {
        searchParams.append('resources', 'all')
      } else {
        this.selectedResources.forEach(resource => {
          searchParams.append(
            'resources[]',
            isObject(resource) ? resource.id.value : resource
          )
        })
      }

      return (
        (this.endpoint || `/nova-api/${this.resourceName}/action`) +
        '?' +
        searchParams.toString()
      )
    },
  },
}
</script>
